import styled, { css } from 'styled-components';
import React from 'react';
import sortBy from 'lodash/sortBy';

import { formatHandicap, removePipes, withRootTestId } from '@williamhill/rosi-ui-shared/src/lib';
import { InjuryProtection as InjuryProtectionIcon } from '@williamhill/rosi-ui-shared/src/assets/icons';
import { iconsByResultType, injuryProtectionIconProps, Leg, struckThroughStyles } from './legs-component';
import { Text } from '@williamhill/rosi-ui-shared/src/components/typography/Typography';

export default function SGPLegsComponent({ legs }: { legs: Leg[] }) {
  return (
    <>
      <SGPLegsList weight="medium" as="ul">
        {sortBy(legs, ({ eventStartTime }) => new Date(eventStartTime).getTime()).map(
          ({ id, market, selection, result, marketLine }) => {
            const withTestId = withRootTestId(`bet-tracker-bets-ticket-leg-${id}`);
            const selectionName = removePipes(selection.name);
            const isInjuryProtected = result?.type === 'injury_protection';
            const isStruckThrough = result?.type === 'void' || isInjuryProtected;

            return (
              <SGPLegItem data-testid={withTestId()} icon={result && iconsByResultType[result.type]} key={id}>
                <section>
                  <main>
                    <SelectionNameContainer
                      weight="bold"
                      data-testid={withTestId('name')}
                      isStruckThrough={isStruckThrough}>
                      {selectionName}
                      {marketLine && ` ${formatHandicap(marketLine, selectionName, false)}`}
                    </SelectionNameContainer>
                    <MarketNameContainer data-testid={withTestId('market')} isStruckThrough={isStruckThrough}>
                      {removePipes(market.name)}
                    </MarketNameContainer>
                    {isInjuryProtected && <InjuryProtectionIcon {...injuryProtectionIconProps} />}
                  </main>
                </section>
              </SGPLegItem>
            );
          }
        )}
      </SGPLegsList>
    </>
  );
}

const SGPLegsList = styled(Text)`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  list-style-type: none;
  margin: 0;
  padding: 0.8rem 0;
`;

const SGPLegItem = styled.li<{ icon?: { color: string; content: string } }>`
  display: flex;
  align-items: center;
  position: relative;
  & > section {
    margin-left: 2.2rem;
  }

  ${({ icon, theme }) => {
    if (icon) {
      return css`
        &:before {
          display: flex;
          justify-content: center;
          background-color: ${theme.colors.modals[icon.color]};
          color: ${theme.colors.white};
          /* prettier-ignore */
          content: "${icon.content}";
          position: absolute;
          padding: 0.1rem;
          border-radius: 50%;
          font-size: 1rem;
          height: 1.3rem;
          width: 1.3rem;
          z-index: 2;
        }
      `;
    }

    return css`
      &:before {
        display: flex;
        justify-content: center;
        background-color: ${theme.colors.modals.neutral};
        /* prettier-ignore */
        content: "";
        position: absolute;
        padding: 0.1rem;
        border-radius: 50%;
        font-size: 1rem;
        height: 1.3rem;
        width: 1.3rem;
        z-index: 2;
      }
    `;
  }};

  &:not(:last-child) {
    ::after {
      border-left: 0.1rem solid ${({ theme }) => theme.colors.modals.neutral};
      /* prettier-ignore */
      content: "";
      display: block;
      height: calc(100% + 1.3rem);
      left: 0.6rem;
      position: absolute;
      top: 1rem;
      z-index: 0;
    }
  }
`;

const SelectionNameContainer = styled(Text)<{ isStruckThrough?: boolean }>`
  flex-grow: 1;
  ${({ isStruckThrough }) => isStruckThrough && struckThroughStyles}
`;

const MarketNameContainer = styled.span<{ isStruckThrough?: boolean }>`
  color: ${({ theme }) => theme.colors.brand.quaternary};
  margin: 0 0.5rem;
  ${({ isStruckThrough }) => isStruckThrough && struckThroughStyles}
`;
